import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import SettingsPage from "./pages/SettingsPage";
import ConfirmEmailPage from "./pages/ConfirmEmailPage";
import ContextPage from "./pages/ContextPage";
import WbsPage from "./pages/WbsPage";
import AdminPage from "./pages/AdminPage";
import TaskStudioPage from "./pages/TaskStudioPage";
import PresentationStudioPage from "./pages/PresentationStudioPage";
import TeamStudioPage from "./pages/TeamStudioPage";
import GanttStudioPage from "./pages/GanttStudioPage";
import BoardPage from "./pages/BoardPage";
import ChatPage from "./pages/ChatPage";
import MeetingPage from "./pages/MeetingPage";
import CheckoutRedirectPage from "./pages/CheckoutRedirectPage";
import AppLayout from "./components/AppLayout";
import {
  BrowserRouter,
  Navigate,
  useNavigate,
  useLocation
} from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_OPENAI_MODEL,
  OPENAI_MODELS,
  ROLE_OWNER,
  VIEW_SCOPE_WORKSTREAM
} from "./domainCatalog";
import { apiGet, apiPost, apiWebSocketUrl, refreshAccessToken } from "./apiClient";
import { PLAN_PRO, clearPendingPlan, readPendingPlan, setPendingPlan } from "./utils/pendingPlan";

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState(localStorage.getItem("access_token"));
  const [subscription, setSubscription] = useState(null);
  const [currentUser, setCurrentUser] = useState(undefined);
  const [availableModels, setAvailableModels] = useState(OPENAI_MODELS);
  const [projects, setProjects] = useState([]);
  const [viewScope, setViewScope] = useState(
    () => localStorage.getItem("studio_view_scope") || VIEW_SCOPE_WORKSTREAM
  );
  const [selectedModel, setSelectedModel] = useState(
    () => localStorage.getItem("selected_openai_model") || DEFAULT_OPENAI_MODEL
  );

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  const [loading, setLoading] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [chatToast, setChatToast] = useState(null);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [meetingToast, setMeetingToast] = useState(null);

  const isAdmin = currentUser?.email === "allanmsales@gmail.com";

  const handleLogin = async () => {
    setLoginError("");
    setLoading(true);

    try {
      const data = await apiPost("/auth/login", { email: loginEmail, password: loginPassword });

      localStorage.setItem("access_token", data.access_token);
      setToken(data.access_token);
      navigate(readPendingPlan() === PLAN_PRO ? "/checkout" : "/task-studio");
    } catch (error) {
      setLoginError(error.message || "Error calling API");
    }

    setLoading(false);
  };

  const handleLogout = async () => {
    try { await apiPost("/auth/logout", undefined, { token }); } catch { /* Clear local state even if logout is unavailable. */ }
    localStorage.removeItem("access_token");
    clearPendingPlan();
    setToken(null);
    setSubscription(null);
    setCurrentUser(null);
    navigate("/");
    setLoginEmail("");
    setLoginPassword("");
    setLoginError("");
    setProjects([]);
  };

  const changeModel = useCallback((model) => {
    setSelectedModel(model);
    localStorage.setItem("selected_openai_model", model);
  }, []);

  const changeViewScope = (scope) => {
    setViewScope(scope);
    localStorage.setItem("studio_view_scope", scope);
  };

  const loadSubscription = useCallback(async () => {
    try {
      const data = await apiGet("/subscription", { token });
      setSubscription(data);
    } catch {
      // silent for now
    }
  }, [token]);

  const loadCurrentUser = useCallback(async () => {
    try {
      setCurrentUser(await apiGet("/auth/me", { token }));
    } catch {
      setCurrentUser(null);
    }
  }, [token]);

  const loadModels = useCallback(async () => {
    try {
      const data = await apiGet("/models", { token });
      setAvailableModels(data.models);
      const isSupported = data.models.some((model) => model.id === selectedModel);
      if (!isSupported) changeModel(data.default_model);
    } catch {
      // Use the bundled model list when the catalog cannot be loaded.
    }
  }, [changeModel, selectedModel, token]);

  const loadProjects = useCallback(async () => {
    try {
      const data = await apiGet("/projects", { token });
      setProjects(data.projects || []);
    } catch {
      // Keep the current project list when the API is temporarily unavailable.
    }
  }, [token]);

  const loadChatSummary = useCallback(async () => {
    if (!token) return null;
    try {
      const data = await apiGet("/chat/participants", { token });
      setChatUnreadCount(data.total_unread || 0);
      return data;
    } catch {
      return null;
    }
  }, [token]);

  const loadActiveMeeting = useCallback(async () => {
    if (!token) return null;
    try {
      const data = await apiGet("/meetings/active", { token });
      setActiveMeeting(data);
      return data;
    } catch {
      return null;
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadSubscription();
      loadCurrentUser();
      loadModels();
      loadProjects();
      loadChatSummary();
      loadActiveMeeting();
    }
  }, [loadActiveMeeting, loadChatSummary, loadCurrentUser, loadModels, loadProjects, loadSubscription, token]);

  useEffect(() => {
    const receiveToken = (event) => setToken(event.detail);
    window.addEventListener("taskit:access-token", receiveToken);
    return () => window.removeEventListener("taskit:access-token", receiveToken);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    const expiresAt = accessTokenExpiry(token);
    const delay = Math.max(5000, expiresAt - Date.now() - 60000);
    const timer = window.setTimeout(() => refreshAccessToken(), delay);
    return () => window.clearTimeout(timer);
  }, [token]);

  useEffect(() => {
    if (!token || !projects.some((project) => project.is_active) || typeof WebSocket === "undefined") return undefined;
    let socket;
    let reconnectTimer;
    let pingTimer;
    let stopped = false;
    const connect = () => {
      socket = new WebSocket(apiWebSocketUrl("/chat/live"));
      socket.onopen = () => {
        socket.send(JSON.stringify({ type: "authenticate", token }));
        loadChatSummary();
        pingTimer = window.setInterval(() => socket.readyState === WebSocket.OPEN && socket.send(JSON.stringify({ type: "ping" })), 25000);
      };
      socket.onmessage = async (event) => {
        let payload;
        try { payload = JSON.parse(event.data); } catch { return; }
        window.dispatchEvent(new CustomEvent("taskit:chat-event", { detail: payload }));
        if (payload.type === "new_message") {
          const summary = await loadChatSummary();
          if (summary && payload.message.sender_member_id !== summary.current_member_id) {
            const sender = summary?.participants?.find((item) => item.member_id === payload.message.sender_member_id);
            setChatToast({ sender: sender?.display_name || "New message", body: payload.message.body });
            window.setTimeout(() => setChatToast(null), 5000);
            if (localStorage.getItem("taskit.chatSound") !== "false") playMessageSound();
          }
        } else if (payload.type === "unread_changed") {
          loadChatSummary();
        } else if (payload.type === "meeting_started") {
          setActiveMeeting(payload.meeting);
          if (location.pathname !== "/meeting") {
            setMeetingToast(payload.meeting);
            window.setTimeout(() => setMeetingToast(null), 10000);
            if (localStorage.getItem("taskit.chatSound") !== "false") playMessageSound();
          }
        } else if (payload.type === "meeting_ended") {
          setActiveMeeting(null);
          setMeetingToast(null);
        }
      };
      socket.onclose = () => {
        if (pingTimer) window.clearInterval(pingTimer);
        if (!stopped) reconnectTimer = window.setTimeout(connect, 2000);
      };
    };
    connect();
    return () => {
      stopped = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (pingTimer) window.clearInterval(pingTimer);
      socket?.close();
    };
  }, [loadChatSummary, location.pathname, projects, token]);

  useEffect(() => {
    document.title = chatUnreadCount ? `(${chatUnreadCount}) Taskit` : "Taskit";
    return () => { document.title = "Taskit"; };
  }, [chatUnreadCount]);

  useEffect(() => {
    const unlockAudio = () => {
      const context = messageAudioContext();
      context?.resume?.().catch?.(() => {});
    };
    window.addEventListener("pointerdown", unlockAudio, { once: true });
    return () => window.removeEventListener("pointerdown", unlockAudio);
  }, []);

  const selectProject = async (projectId) => {
    try {
      await apiPost(`/projects/${projectId}/select`, undefined, { token });
      window.location.reload();
    } catch {
      // Keep the current workstream selected when the API rejects the request.
    }
  };

  const createProject = async () => {
    const name = window.prompt("Workstream / epic name");
    if (!name?.trim()) return;
    let memberIds = [];
    try {
      const workspace = await apiGet("/workspace/members", { token });
      if (workspace.current_role === ROLE_OWNER && workspace.members?.length) {
        const options = workspace.members
          .filter((member) => member.role !== "owner")
          .map((member) => member.email)
          .join(", ");
        if (options) {
          const selectedEmails = window.prompt(
            `Grant access to which members? Enter emails separated by comma.\nAvailable: ${options}`,
            ""
          );
          const selectedSet = new Set(
            (selectedEmails || "").split(",").map((email) => email.trim().toLowerCase()).filter(Boolean)
          );
          memberIds = workspace.members
            .filter((member) => selectedSet.has(member.email.toLowerCase()))
            .map((member) => member.id);
        }
      }
    } catch {
      // Workstream creation can continue without optional member access selection.
    }
    try {
      await apiPost("/projects", { name: name.trim(), member_ids: memberIds }, { token });
      window.location.reload();
    } catch (error) {
      window.alert(error.message || "Could not create workstream.");
    }
  };

  const projectLayoutProps = {
    projects,
    activeProjectId: projects.find((project) => project.is_active)?.id,
    onProjectChange: selectProject,
    onCreateProject: createProject,
    viewScope,
    onViewScopeChange: changeViewScope
  };

  const studioLayoutProps = {
    ...projectLayoutProps,
    subscription,
    onOpenChat: () => navigate("/chat"),
    onOpenMeeting: () => navigate("/meeting"),
    onOpenTasks: () => navigate("/task-studio"),
    onOpenBoard: () => navigate("/board"),
    onOpenContext: () => navigate("/context"),
    onOpenWbs: () => navigate("/wbs"),
    onOpenTeam: () => navigate("/team-studio"),
    onOpenGantt: () => navigate("/gantt-studio"),
    onOpenPresentation: () => navigate("/presentation-studio"),
    onOpenAdmin: () => navigate("/admin"),
    isAdmin,
    onOpenSettings: () => navigate("/settings"),
    availableModels,
    selectedModel,
    onModelChange: changeModel,
    onLogout: handleLogout,
    chatUnreadCount,
    chatToast,
    onOpenChatToast: () => { setChatToast(null); navigate("/chat"); },
    activeMeeting,
    meetingToast,
    onOpenMeetingToast: () => { setMeetingToast(null); navigate("/meeting"); }
  };

  const renderStudioPage = (children) => {
    if (!token) {
      return <Navigate to="/login" />;
    }

    return (
      <AppLayout {...studioLayoutProps}>
        {children}
      </AppLayout>
    );
  };

  if (location.pathname === "/") {
    return (
      <LandingPage
        onStart={() => navigate("/signup")}
        onStartPro={() => {
          setPendingPlan(PLAN_PRO);
          navigate(token ? "/checkout" : "/signup?plan=pro");
        }}
        onLogin={() => navigate("/login")}
      />
    );
  }

  if (location.pathname === "/signup") {
    return (
      <SignupPage
        onBackToLogin={() => navigate("/login")}
      />
    );
  }

  if (location.pathname === "/context") {
    return renderStudioPage(
      <ContextPage token={token} model={selectedModel} scope={viewScope} onUsageChange={loadSubscription} />
    );
  }

  if (location.pathname === "/wbs") {
    return renderStudioPage(
      <WbsPage token={token} model={selectedModel} scope={viewScope} onUsageChange={loadSubscription} />
    );
  }

  if (location.pathname === "/admin") {
    if (!token) {
      return <Navigate to="/login" />;
    }
    if (currentUser === undefined) {
      return null;
    }
    if (!isAdmin) {
      return <Navigate to="/task-studio" />;
    }

    return renderStudioPage(<AdminPage token={token} />);
  }

  if (location.pathname === "/login") {
    return (
      <LoginPage
        email={loginEmail}
        password={loginPassword}
        error={loginError}
        loading={loading}
        onEmailChange={setLoginEmail}
        onPasswordChange={setLoginPassword}
        onLogin={handleLogin}
        onSignup={() => navigate("/signup")}
      />
    );
  }

  if (location.pathname === "/checkout") {
    if (!token) {
      return <Navigate to="/login" />;
    }

    return (
      <CheckoutRedirectPage
        token={token}
        onCancelled={() => navigate("/task-studio")}
      />
    );
  }

  if (location.pathname === "/confirm") {
    return (
      <ConfirmEmailPage
        onConfirmed={(accessToken) => {
          localStorage.setItem("access_token", accessToken);
          setToken(accessToken);
          navigate(readPendingPlan() === PLAN_PRO ? "/checkout" : "/task-studio");
        }}
        onGoToLogin={() => navigate("/login")}
      />
    );
  }

  if (location.pathname === "/settings" || location.pathname === "/settings/github/callback") {
    if (!token) {
      return <Navigate to="/login" />;
    }
    if (subscription && subscription.current_role !== "owner") {
      return <Navigate to="/task-studio" />;
    }

    return renderStudioPage(<SettingsPage token={token} />);
  }

  if (location.pathname === "/chat") {
    return renderStudioPage(<ChatPage token={token} onUnreadChange={setChatUnreadCount} />);
  }

  if (location.pathname === "/meeting") {
    return renderStudioPage(
      <MeetingPage token={token} activeMeeting={activeMeeting} onMeetingChange={setActiveMeeting} />
    );
  }

  if (location.pathname === "/task-studio") {
    return renderStudioPage(
      <TaskStudioPage token={token} model={selectedModel} scope={viewScope} onUsageChange={loadSubscription} />
    );
  }

  if (location.pathname === "/board") {
    return renderStudioPage(
      <BoardPage token={token} scope={viewScope} onOpenTicket={() => navigate("/task-studio")} />
    );
  }

  if (location.pathname === "/team-studio") {
    return renderStudioPage(<TeamStudioPage token={token} />);
  }

  if (location.pathname === "/gantt-studio") {
    return renderStudioPage(
      <GanttStudioPage token={token} model={selectedModel} onUsageChange={loadSubscription} />
    );
  }

  if (location.pathname === "/presentation-studio") {
    return renderStudioPage(
      <PresentationStudioPage token={token} model={selectedModel} scope={viewScope} onUsageChange={loadSubscription} />
    );
  }

  return <Navigate to="/" />;

}

let sharedMessageAudioContext;
const messageAudioContext = () => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return null;
  sharedMessageAudioContext = sharedMessageAudioContext || new AudioContext();
  return sharedMessageAudioContext;
};

const playMessageSound = () => {
  try {
    const context = messageAudioContext();
    if (!context) return;
    context.resume?.().catch?.(() => {});
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = 660;
    gain.gain.setValueAtTime(0.07, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.18);
    oscillator.connect(gain); gain.connect(context.destination); oscillator.start(); oscillator.stop(context.currentTime + 0.18);
  } catch {
    // Browsers may block sound until the user has interacted with the page.
  }
};

const accessTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(window.atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return Number(payload.exp || 0) * 1000;
  } catch {
    return Date.now();
  }
};

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
